import { Controller } from "@hotwired/stimulus"

// Controller for managing inline todos on project page
export default class extends Controller {
  static targets = [
    "list", "emptyState", "searchInput", "personFilter", "statusFilter",
    "pagination", "pageInfo", "prevButton", "nextButton",
    "modalContainer", "modalContent", "loadingIndicator"
  ]
  static values = { 
    projectId: Number,
    currentPage: { type: Number, default: 1 }
  }
  
  connect() {
    this.loadTodos()
    document.addEventListener("keydown", this.handleKeydown.bind(this))
  }
  
  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown.bind(this))
  }
  
  // Load todos from server
  async loadTodos() {
    const params = new URLSearchParams()
    params.set("page", this.currentPageValue)
    
    if (this.hasSearchInputTarget && this.searchInputTarget.value) {
      params.set("q", this.searchInputTarget.value)
    }
    if (this.hasPersonFilterTarget && this.personFilterTarget.value) {
      params.set("person_id", this.personFilterTarget.value)
    }
    if (this.hasStatusFilterTarget && this.statusFilterTarget.value) {
      params.set("status", this.statusFilterTarget.value)
    }
    
    try {
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.classList.remove("hidden")
      }
      
      const response = await fetch(`/projects/${this.projectIdValue}/todos?${params}`, {
        headers: { "Accept": "application/json" }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.renderTodos(data.todos)
        this.renderPagination(data.pagination)
        this.populatePersonFilter(data.people)
      }
    } catch (error) {
      console.error("Error loading todos:", error)
    } finally {
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.classList.add("hidden")
      }
    }
  }
  
  // Render todos list
  renderTodos(todos) {
    if (todos.length === 0) {
      this.listTarget.classList.add("hidden")
      if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.remove("hidden")
      }
      return
    }
    
    this.listTarget.classList.remove("hidden")
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
    
    this.listTarget.innerHTML = todos.map(todo => this.todoHTML(todo)).join("")
  }
  
  // Generate HTML for a single todo
  todoHTML(todo) {
    const checkboxClass = todo.completed 
      ? "border-green-500 bg-green-500" 
      : todo.overdue 
        ? "border-red-400" 
        : "border-slate-300"
    
    const titleClass = todo.completed 
      ? "text-slate-400 line-through" 
      : "text-slate-900"
    
    const dueDateClass = todo.overdue ? "text-red-600" : "text-slate-500"
    
    return `
      <div class="p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group"
           data-todo-id="${todo.id}"
           data-completed="${todo.completed}">
        <div class="flex items-start gap-3">
          <!-- Checkbox -->
          <button type="button" 
                  class="mt-0.5 focus:outline-none cursor-pointer" 
                  data-action="project-todos#toggleComplete"
                  title="${todo.completed ? 'Mark as pending' : 'Mark as complete'}">
            ${todo.completed ? `
              <div class="w-4 h-4 rounded border-2 border-green-500 bg-green-500 flex items-center justify-center">
                <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ` : `
              <div class="w-4 h-4 rounded border-2 ${checkboxClass} hover:border-blue-500"></div>
            `}
          </button>

          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium ${titleClass} truncate">${this.escapeHtml(todo.title)}</p>
            <div class="flex items-center gap-2 mt-1 text-xs flex-wrap">
              ${todo.due_at_display ? `<span class="${dueDateClass}">${todo.due_at_display}</span>` : ''}
              ${todo.person ? `<span class="text-slate-400">· ${this.escapeHtml(todo.person.name)}</span>` : ''}
              ${todo.meeting ? `<span class="text-slate-400">· ${this.escapeHtml(todo.meeting.title)}</span>` : ''}
              ${this.videoTimestampHTML(todo)}
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button type="button" 
                    data-action="project-todos#openEditModal"
                    data-url="${todo.edit_url}"
                    class="p-1 text-slate-400 hover:text-blue-600 rounded cursor-pointer" 
                    title="Edit">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            ${todo.completed ? `
              <button type="button" 
                      data-action="project-todos#archiveTodo"
                      class="p-1 text-slate-400 hover:text-amber-600 rounded cursor-pointer" 
                      title="Archive">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </button>
            ` : ''}
            <button type="button" 
                    data-action="project-todos#deleteTodo"
                    class="p-1 text-slate-400 hover:text-red-600 rounded cursor-pointer" 
                    title="Delete">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `
  }
  
  // Render pagination controls
  renderPagination(pagination) {
    if (!this.hasPaginationTarget) return
    
    if (pagination.total_pages <= 1) {
      this.paginationTarget.classList.add("hidden")
      return
    }
    
    this.paginationTarget.classList.remove("hidden")
    
    if (this.hasPageInfoTarget) {
      const from = ((pagination.current_page - 1) * pagination.per_page) + 1
      const to = Math.min(pagination.current_page * pagination.per_page, pagination.total_count)
      this.pageInfoTarget.textContent = `${from}-${to} of ${pagination.total_count}`
    }
    
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = pagination.current_page <= 1
      this.prevButtonTarget.classList.toggle("opacity-50", pagination.current_page <= 1)
    }
    
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = pagination.current_page >= pagination.total_pages
      this.nextButtonTarget.classList.toggle("opacity-50", pagination.current_page >= pagination.total_pages)
    }
  }
  
  // Populate person filter dropdown
  populatePersonFilter(people) {
    if (!this.hasPersonFilterTarget) return
    
    const currentValue = this.personFilterTarget.value
    const options = ['<option value="">All People</option>']
    
    people.forEach(person => {
      const selected = person.id.toString() === currentValue ? 'selected' : ''
      options.push(`<option value="${person.id}" ${selected}>${this.escapeHtml(person.name)}</option>`)
    })
    
    this.personFilterTarget.innerHTML = options.join("")
  }
  
  // Filter handlers
  search() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.currentPageValue = 1
      this.loadTodos()
    }, 300)
  }
  
  filterByPerson() {
    this.currentPageValue = 1
    this.loadTodos()
  }
  
  filterByStatus() {
    this.currentPageValue = 1
    this.loadTodos()
  }
  
  // Pagination handlers
  prevPage() {
    if (this.currentPageValue > 1) {
      this.currentPageValue--
      this.loadTodos()
    }
  }
  
  nextPage() {
    this.currentPageValue++
    this.loadTodos()
  }
  
  // Toggle todo completion
  async toggleComplete(event) {
    event.preventDefault()
    const todoItem = event.currentTarget.closest("[data-todo-id]")
    const todoId = todoItem.dataset.todoId
    const isCompleted = todoItem.dataset.completed === "true"
    
    const url = isCompleted ? `/todos/${todoId}/reopen` : `/todos/${todoId}/complete`
    
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })
      
      if (response.ok) {
        this.loadTodos() // Reload to get fresh data
      }
    } catch (error) {
      console.error("Error toggling todo:", error)
    }
  }
  
  // Archive todo
  async archiveTodo(event) {
    event.preventDefault()
    const todoItem = event.currentTarget.closest("[data-todo-id]")
    const todoId = todoItem.dataset.todoId
    
    try {
      const response = await fetch(`/todos/${todoId}/archive`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })
      
      if (response.ok) {
        // Animate removal
        todoItem.style.transition = "opacity 0.3s, transform 0.3s"
        todoItem.style.opacity = "0"
        todoItem.style.transform = "translateX(-10px)"
        
        setTimeout(() => this.loadTodos(), 300)
      }
    } catch (error) {
      console.error("Error archiving todo:", error)
    }
  }
  
  // Delete todo
  async deleteTodo(event) {
    event.preventDefault()
    
    if (!confirm("Are you sure you want to delete this todo?")) {
      return
    }
    
    const todoItem = event.currentTarget.closest("[data-todo-id]")
    const todoId = todoItem.dataset.todoId
    
    try {
      const response = await fetch(`/todos/${todoId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })
      
      if (response.ok) {
        todoItem.style.transition = "opacity 0.3s, transform 0.3s"
        todoItem.style.opacity = "0"
        todoItem.style.transform = "translateX(-10px)"
        
        setTimeout(() => this.loadTodos(), 300)
      }
    } catch (error) {
      console.error("Error deleting todo:", error)
    }
  }
  
  // Open edit modal
  async openEditModal(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    
    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        this.modalContentTarget.innerHTML = html
        this.modalContainerTarget.classList.remove("hidden")
        document.body.classList.add("overflow-hidden")
        
        const form = this.modalContentTarget.querySelector("form")
        if (form) {
          form.addEventListener("submit", this.handleFormSubmit.bind(this))
        }
      }
    } catch (error) {
      console.error("Error opening modal:", error)
    }
  }
  
  // Open add modal
  async openAddModal(event) {
    event.preventDefault()
    const url = `/todos/new?project_id=${this.projectIdValue}`
    
    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        this.modalContentTarget.innerHTML = html
        this.modalContainerTarget.classList.remove("hidden")
        document.body.classList.add("overflow-hidden")
        
        const form = this.modalContentTarget.querySelector("form")
        if (form) {
          form.addEventListener("submit", this.handleFormSubmit.bind(this))
        }
      }
    } catch (error) {
      console.error("Error opening add modal:", error)
    }
  }
  
  // Handle form submission
  async handleFormSubmit(event) {
    event.preventDefault()
    const form = event.target
    const formData = new FormData(form)
    
    try {
      const response = await fetch(form.action, {
        method: form.method || "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })
      
      if (response.ok) {
        this.closeModal()
        this.loadTodos()
      } else {
        const html = await response.text()
        this.modalContentTarget.innerHTML = html
        const newForm = this.modalContentTarget.querySelector("form")
        if (newForm) {
          newForm.addEventListener("submit", this.handleFormSubmit.bind(this))
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }
  
  // Close modal
  closeModal(event) {
    if (event) event.preventDefault()
    this.modalContainerTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this.modalContentTarget.innerHTML = ""
  }
  
  // Close modal on background click
  closeModalBackground(event) {
    if (event.target === this.modalContainerTarget) {
      this.closeModal()
    }
  }
  
  // Handle escape key
  handleKeydown(event) {
    if (event.key === "Escape" && !this.modalContainerTarget.classList.contains("hidden")) {
      this.closeModal()
    }
  }
  
  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
  
  // Generate video timestamp link HTML
  videoTimestampHTML(todo) {
    if (!todo.has_video_timestamp) return ''
    
    if (todo.video_expired) {
      return `
        <span class="inline-flex items-center text-xs text-slate-400" title="Video recording has expired">
          <svg class="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <span class="line-through">${todo.video_timestamp_display}</span>
        </span>
      `
    }
    
    return `
      <a href="${todo.video_url_with_timestamp}" 
         class="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline"
         title="Watch at ${todo.video_timestamp_display} in meeting recording">
        <svg class="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        ${todo.video_timestamp_display}
      </a>
    `
  }
  
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}

